import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { leadgenApi, type LeadGenForm } from '@/api/leadgen.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showApiError } from '@/utils/apiError';

interface FormField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
}

function getCookieId() {
  const key = 'lg_visitor';
  let value = localStorage.getItem(key);
  if (!value) {
    value = crypto.randomUUID();
    localStorage.setItem(key, value);
  }
  return value;
}

export function LeadGenPublicSubmitPage() {
  const { publicKey } = useParams<{ publicKey: string }>();
  const [form, setForm] = useState<LeadGenForm | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileId, setProfileId] = useState<number | undefined>();
  const cookieId = useMemo(() => getCookieId(), []);

  const parsedFields: FormField[] = useMemo(() => {
    if (!form?.fieldsJson) {
      return [
        { key: 'name', label: 'Full name', type: 'text', required: true },
        { key: 'email', label: 'Email', type: 'email', required: true },
        { key: 'phone', label: 'Phone', type: 'phone' },
      ];
    }
    try {
      return JSON.parse(form.fieldsJson) as FormField[];
    } catch {
      return [];
    }
  }, [form]);

  useEffect(() => {
    if (!publicKey) return;
    leadgenApi.getPublicForm(publicKey)
      .then(setForm)
      .catch((err) => showApiError(err, 'Form not found'));
  }, [publicKey]);

  useEffect(() => {
    if (!publicKey) return;
    leadgenApi.trackVisitor({ formPublicKey: publicKey, cookieId })
      .then((profile) => setProfileId(profile.id))
      .catch(() => undefined);
  }, [publicKey, cookieId]);

  const submit = async () => {
    if (!publicKey || !consent) return;
    for (const field of parsedFields) {
      if (field.required && !fields[field.key]?.trim()) {
        showApiError(new Error(`Missing required field: ${field.label}`), 'Validation failed');
        return;
      }
    }
    setSaving(true);
    try {
      const params = new URLSearchParams(window.location.search);
      await leadgenApi.submitPublic({
        formPublicKey: publicKey,
        fields,
        consentGiven: true,
        visitorProfileId: profileId,
        visitorCookieId: cookieId,
        utmSource: params.get('utm_source') ?? undefined,
        utmMedium: params.get('utm_medium') ?? undefined,
        utmCampaign: params.get('utm_campaign') ?? undefined,
        utmTerm: params.get('utm_term') ?? undefined,
        utmContent: params.get('utm_content') ?? undefined,
      });
      if (fields.email) {
        await leadgenApi.savePublicConsent({
          formPublicKey: publicKey,
          email: fields.email,
          consentVersion: '1.0',
          cookieConsent: true,
          privacyAcceptance: true,
        }).catch(() => undefined);
      }
      setDone(true);
    } catch (err) {
      showApiError(err, 'Submission failed');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
        <div className="card max-w-md w-full p-8 text-center">
          <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Thank you</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">We received your request and will be in touch shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="card max-w-md w-full p-8 space-y-4">
        <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100">{form?.name ?? 'Contact us'}</h1>
        {form?.description && <p className="text-sm text-slate-500 dark:text-slate-400">{form.description}</p>}
        {parsedFields.map((field) => (
          <Input
            key={field.key}
            label={field.label}
            type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
            value={fields[field.key] ?? ''}
            onChange={(e) => setFields({ ...fields, [field.key]: e.target.value })}
          />
        ))}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          I consent to my data being processed (GDPR)
        </label>
        <Button onClick={submit} loading={saving} disabled={!consent || !form}>
          Submit
        </Button>
      </div>
    </div>
  );
}
